import {
    PER_DAY,
    TWO_DAYS,
    THREE_DAYS,
    FOUR_DAYS,
    FIVE_DAYS,
    WEEKLY_PRICE,
    OVERNIGHT_PRICE,
    TWENTY_EIGHT_DAYS,
    DAY_PACKAGES,
    PER_HOUR,
    PER_MINUTE,
} from './constants';

export const getUnitType = (itemPackage) => {
    const unitType = itemPackage === 'perMinutePrice'
        ? 'minute' : itemPackage === 'perHourPrice'
            ? 'hour' : itemPackage === 'perDayPrice'
                ? 'day' : itemPackage === 'overnightPrice'
                    ? 'night' : itemPackage === 'twoDaysPrice'
                        ? 'twoDays' : itemPackage === 'threeDaysPrice'
                            ? 'threeDays' : itemPackage === 'fourDaysPrice'
                                ? 'fourDays' : itemPackage === 'fiveDaysPrice'
                                    ? 'fiveDays' : itemPackage === 'oneWeekPrice'
                                        ? 'perWeek' : itemPackage === 'twentyEightDaysPrice'
                                            ? 'twentyEightDays' : null;
    return unitType;
};

export function getMainItem(lineItems) {
    return lineItems.find(lineItem => lineItem.item === true);
}

// RAS-2510: duration (in days) each day-tier package represents. Mirrors
// OrderPanel.js's local PACKAGE_ORDER/getPackageDayCount - including
// overnightPrice's special-cased 1-day duration, since it's part of DAY_PACKAGES but
// (like in OrderPanel.js) isn't itself a {key,days} pair - confirmed by reading
// OrderPanel.js rather than guessed.
const DAY_PACKAGE_DURATIONS = {
    [PER_DAY]: 1,
    [OVERNIGHT_PRICE]: 1,
    [TWO_DAYS]: 2,
    [THREE_DAYS]: 3,
    [FOUR_DAYS]: 4,
    [FIVE_DAYS]: 5,
    [WEEKLY_PRICE]: 7,
    [TWENTY_EIGHT_DAYS]: 28,
};

// RAS-2510: there is no separate sub-hour tier (e.g. a "per30Minutes" package) in
// this codebase's publicData.prices - only perHourPrice (1 hour) and perMinutePrice
// (1 minute) exist, confirmed via OrderPanel.js/src/util/constants.js.
const HOURLY_PACKAGE_MINUTES = {
    [PER_MINUTE]: 1,
    [PER_HOUR]: 60,
};

const isPackageAvailable = (prices, packageKey) => !!prices?.[packageKey];

/**
 * RAS-2520: the booking categories a rental can be made in. A listing's
 * publicData.prices can carry tiers from more than one at once (e.g. both perDayPrice
 * and perHourPrice), which is what the booking panel's category toggle lets the renter
 * switch between.
 *
 * OVERNIGHT is deliberately its OWN category rather than a daily tier: overnightPrice is
 * charged per NIGHT, not per day. It is a distinct unit end-to-end - server/api-util/
 * lineItems.js maps it to the `night` unitType, alongside day/hour/minute - and, unlike
 * every genuine day tier, its duration cannot be inferred from the picked range: one
 * night and one day span the same calendar dates. Duration alone therefore can never
 * disambiguate it, which is exactly why it needs a pill of its own; both the client's
 * getRentalPackage (util/data.js) and the server's only ever resolve to overnightPrice
 * when the client explicitly sends selectedPackage === overnightPrice.
 */
export const BOOKING_CATEGORY_DAILY = 'DAILY';
export const BOOKING_CATEGORY_OVERNIGHT = 'OVERNIGHT';
export const BOOKING_CATEGORY_HOURLY = 'HOURLY';
export const BOOKING_CATEGORY_MINUTE = 'MINUTE';

// Display/selection order for the toggle, longest unit first: daily, overnight, hourly,
// minute. Overnight sits next to daily because both are picked on the date calendar.
export const BOOKING_CATEGORY_ORDER = [
    BOOKING_CATEGORY_DAILY,
    BOOKING_CATEGORY_OVERNIGHT,
    BOOKING_CATEGORY_HOURLY,
    BOOKING_CATEGORY_MINUTE,
];

/**
 * The price keys belonging to each category. Confirmed by reading OrderPanel.js's own
 * PACKAGE_ORDER/DAY_PACKAGES and src/util/constants.js rather than guessed - note that
 * publicData.prices is an OBJECT keyed by these strings (not an array), and that the
 * keys all carry a "Price" suffix (perDayPrice, oneWeekPrice, ...). There is no
 * sub-hour ("per30Minutes") tier anywhere in this codebase - only perHourPrice and
 * perMinutePrice - so HOURLY is a single-key category, same as MINUTE and OVERNIGHT.
 *
 * DAILY is DAY_PACKAGES minus overnightPrice. DAY_PACKAGES itself deliberately still
 * CONTAINS overnightPrice and is left alone: it answers "is this booked on the date
 * calendar / does it have a day-scale duration", which is true of overnight too and is
 * what availability minimums (getListingDayMinimum below), OrderPanel's
 * shouldHaveBookingDates and the server's own duration handling all rely on. This map
 * answers the different question of "which rate did the renter choose".
 */
export const PRICE_KEYS_BY_BOOKING_CATEGORY = {
    [BOOKING_CATEGORY_DAILY]: DAY_PACKAGES.filter(key => key !== OVERNIGHT_PRICE),
    [BOOKING_CATEGORY_OVERNIGHT]: [OVERNIGHT_PRICE],
    [BOOKING_CATEGORY_HOURLY]: [PER_HOUR],
    [BOOKING_CATEGORY_MINUTE]: [PER_MINUTE],
};

const BOOKING_CATEGORY_BY_PRICE_KEY = BOOKING_CATEGORY_ORDER.reduce((acc, category) => {
    PRICE_KEYS_BY_BOOKING_CATEGORY[category].forEach(key => {
        acc[key] = category;
    });
    return acc;
}, {});

/**
 * RAS-2520: the category a single price key belongs to, e.g. 'oneWeekPrice' -> 'DAILY'.
 * Used to seed the booking panel's category toggle from the listing's defaultPriceKey so
 * the toggle and the existing prefill ladder start in the same state.
 *
 * @param {string} defaultPriceKey - a publicData.prices key
 * @returns {'DAILY'|'OVERNIGHT'|'HOURLY'|'MINUTE'|null} null for an unknown/missing key
 */
export const getDefaultCategory = defaultPriceKey =>
    BOOKING_CATEGORY_BY_PRICE_KEY[defaultPriceKey] || null;

/**
 * RAS-2520: the distinct booking categories a listing actually sells, in
 * BOOKING_CATEGORY_ORDER. A listing offering perDayPrice + oneWeekPrice + perHourPrice
 * returns ['DAILY', 'HOURLY'] - one entry per category, not per tier.
 *
 * Returns [] for missing/empty prices, which is what makes the toggle a no-op for
 * single-tier listings: BookingCategoryToggle renders nothing below 2 categories.
 *
 * @param {Object} prices - publicData.prices (an object map of priceKey -> price)
 * @returns {Array<'DAILY'|'OVERNIGHT'|'HOURLY'|'MINUTE'>}
 */
export const getBookingCategories = prices => {
    if (!prices || typeof prices !== 'object') return [];

    const present = new Set(
        Object.keys(prices)
            .filter(key => !!prices[key])
            .map(key => BOOKING_CATEGORY_BY_PRICE_KEY[key])
            .filter(Boolean)
    );

    return BOOKING_CATEGORY_ORDER.filter(category => present.has(category));
};

/**
 * RAS-2520: the price keys a listing offers within one category, in the canonical tier
 * order (DAY_PACKAGES for daily). Lets the booking panel pick a sensible package when
 * the renter switches category before picking any dates, and lets the price pill span
 * only the active category's tiers.
 *
 * @param {Object} prices - publicData.prices, or OrderPanel's converted pricePackages
 * @param {'DAILY'|'OVERNIGHT'|'HOURLY'|'MINUTE'} category
 * @returns {Array<string>}
 */
export const getBookingCategoryPriceKeys = (prices, category) =>
    (PRICE_KEYS_BY_BOOKING_CATEGORY[category] || []).filter(key =>
        isPackageAvailable(prices, key)
    );

/**
 * RAS-2510: a listing's minimum bookable duration in days, from the day-tier
 * packages available in its publicData.prices - the SHORTEST package it offers (e.g.
 * a listing with only twoDaysPrice + oneWeekPrice has a 2-day minimum, not 1, since
 * it doesn't offer perDayPrice/overnightPrice). Returns null if the listing has no
 * day-tier packages.
 */
export const getListingDayMinimum = prices => {
    const available = DAY_PACKAGES.filter(key => isPackageAvailable(prices, key));
    if (!available.length) return null;
    return Math.min(...available.map(key => DAY_PACKAGE_DURATIONS[key]));
};

/**
 * RAS-2510: a listing's minimum bookable duration in minutes, from the hourly-tier
 * packages available in its publicData.prices. Returns null if the listing has
 * neither perHourPrice nor perMinutePrice.
 */
export const getListingHourlyMinimumMinutes = prices => {
    const available = [PER_MINUTE, PER_HOUR].filter(key => isPackageAvailable(prices, key));
    if (!available.length) return null;
    return Math.min(...available.map(key => HOURLY_PACKAGE_MINUTES[key]));
};

/**
 * RAS-2510: group-level minimum bookable duration for a cart-item group - the
 * HIGHEST per-listing minimum wins (the most restrictive listing in the group sets
 * the floor for everyone in it, since a date edit moves every item in the group to
 * the same new dates together). Day-based and hourly groups are never
 * cross-compared - callers pass `packageType` (e.g. from
 * listingAvailabilityHelpers.js's determinePackageType) to pick the tier.
 *
 * @param {Array<Object>} listings - denormalised listing entities (`.attributes.publicData.prices`)
 * @param {'daily'|'hourly'} packageType
 * @returns {{ unit: 'days'|'minutes', value: number } | null}
 */
export const getGroupMinimumDuration = (listings, packageType) => {
    const pricesList = (listings || [])
        .map(listing => listing?.attributes?.publicData?.prices)
        .filter(Boolean);

    if (packageType === 'hourly') {
        const minutesList = pricesList.map(getListingHourlyMinimumMinutes).filter(v => v != null);
        return minutesList.length ? { unit: 'minutes', value: Math.max(...minutesList) } : null;
    }

    const daysList = pricesList.map(getListingDayMinimum).filter(v => v != null);
    return daysList.length ? { unit: 'days', value: Math.max(...daysList) } : null;
};